import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import { Bell, Plus, Trash2, Play, History } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import PageLayout from '../components/PageLayout';

const TASK_TYPES = [
  { value: 'incomplete_profile',  label: 'Incomplete Profile',       description: 'Participant hasn\'t filled in their profile' },
  { value: 'unsigned_waiver',     label: 'Unsigned Waiver',          description: 'Participant hasn\'t signed the liability waiver' },
  { value: 'missing_flight',      label: 'Missing Flight Info',      description: 'Participant hasn\'t submitted flight details' },
  { value: 'incomplete_tasks',    label: 'Any Incomplete Task',      description: 'Catch-all for any outstanding checklist item' },
  { value: 'incomplete_assignment', label: 'Incomplete Assignment',  description: 'Participant has a pending admin assignment' },
  { value: 'trip_countdown',      label: 'Trip Countdown',           description: 'General reminder — trip is coming up' },
  { value: 'custom',              label: 'Custom Message',           description: 'Send a custom message on your schedule' },
];

const emptyForm = {
  reminder_type: '',
  due_weeks_before_trip: 12,
  interval_weeks: 3,
  enabled: true,
  message_template: '',
};

const ReminderSettingsPage = () => {
  const { logout } = useAuth();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  const [runningJob, setRunningJob] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { fetchSettings(); fetchJobHistory(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/reminders/settings');
      setSettings(res.data);
    } catch { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  };

  const fetchJobHistory = async () => {
    try {
      const res = await api.get('/reminders/job-history');
      setJobHistory(res.data);
    } catch {}
  };

  const openCreate = () => {
    setEditingSetting(null);
    setFormData(emptyForm);
    setShowDialog(true);
  };

  const openEdit = (s) => {
    setEditingSetting(s);
    setFormData({
      reminder_type: s.reminder_type || '',
      due_weeks_before_trip: s.due_weeks_before_trip ?? 12,
      interval_weeks: s.interval_weeks ?? Math.ceil((s.interval_days || 21) / 7),
      enabled: s.enabled !== false,
      message_template: s.message_template || '',
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.reminder_type) { toast.error('Please select a task type'); return; }
    const payload = {
      ...formData,
      interval_days: (formData.interval_weeks || 1) * 7,
    };
    try {
      if (editingSetting) {
        await api.put(`/reminders/settings/${editingSetting.setting_id}`, payload);
        toast.success('Reminder updated');
      } else {
        await api.post('/reminders/settings', payload);
        toast.success('Reminder created');
      }
      setShowDialog(false);
      fetchSettings();
    } catch { toast.error('Failed to save reminder'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this reminder?')) return;
    try {
      await api.delete(`/reminders/settings/${id}`);
      toast.success('Deleted');
      fetchSettings();
    } catch { toast.error('Failed to delete'); }
  };

  const handleToggle = async (s) => {
    try {
      await api.put(`/reminders/settings/${s.setting_id}`, { ...s, enabled: !s.enabled });
      fetchSettings();
    } catch { toast.error('Failed to update'); }
  };

  const handleRunNow = async () => {
    setRunningJob(true);
    try {
      await api.post('/reminders/run-now');
      toast.success('Running reminders manually — check history for results.');
      setTimeout(() => { fetchJobHistory(); setRunningJob(false); }, 3000);
    } catch { toast.error('Failed to run'); setRunningJob(false); }
  };

  const set = (k, v) => setFormData(f => ({ ...f, [k]: v }));
  const taskLabel = (type) => TASK_TYPES.find(t => t.value === type)?.label || type;

  // Preview example for selected reminder
  const previewText = () => {
    if (!formData.reminder_type || !formData.due_weeks_before_trip || !formData.interval_weeks) return null;
    const dueWeeks = parseInt(formData.due_weeks_before_trip);
    const interval = parseInt(formData.interval_weeks);
    const remindersBeforeDue = Math.floor(dueWeeks / interval);
    return `Starting ${interval} week${interval !== 1 ? 's' : ''} after invitation, sends every ${interval} week${interval !== 1 ? 's' : ''} — up to ~${remindersBeforeDue} reminder${remindersBeforeDue !== 1 ? 's' : ''} before the due date. Stops when task is complete.`;
  };

  const reminderActions = (
    <>
      <Button variant="outline" size="sm" className="rounded-full h-8 text-xs"
        onClick={() => { setShowHistory(true); fetchJobHistory(); }}>
        <History className="w-3.5 h-3.5 mr-1.5" />History
      </Button>
      <Button variant="outline" size="sm" className="rounded-full h-8 text-xs"
        onClick={handleRunNow} disabled={runningJob}>
        <Play className="w-3.5 h-3.5 mr-1.5" />
        {runningJob ? 'Running...' : 'Run Manually'}
      </Button>
      <Button size="sm" className="rounded-full bg-primary h-8" onClick={openCreate}>
        <Plus className="w-3.5 h-3.5 mr-1.5" />New Reminder
      </Button>
    </>
  );

  return (
    <PageLayout title="Email Reminders" headerActions={reminderActions} maxWidth="max-w-3xl">
      <>

          {/* How it works */}
          <Card className="mb-6 border-blue-100 bg-blue-50">
            <CardContent className="pt-4 pb-3">
              <div className="flex gap-3">
                <Bell className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-700 space-y-1">
                  <p className="font-medium">How reminders work</p>
                  <p className="text-sm text-blue-600">
                    When a participant is invited to a trip, the reminder clock starts. The system sends an email
                    at your chosen interval (e.g. every 3 weeks) until the task is completed or the due date passes.
                    Use <code className="bg-blue-100 px-1 rounded">{'{name}'}</code> and{' '}
                    <code className="bg-blue-100 px-1 rounded">{'{trip_name}'}</code> in custom messages.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Reminder list */}
          {loading ? (
            <div className="text-center py-12 text-slate-400">Loading...</div>
          ) : settings.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <Bell className="w-10 h-10 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500 mb-4">No reminders configured yet</p>
              <Button onClick={openCreate} className="rounded-full bg-primary">
                <Plus className="w-4 h-4 mr-2" />Create First Reminder
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {settings.map(s => {
                const intervalWeeks = s.interval_weeks ?? Math.ceil((s.interval_days || 21) / 7);
                const dueWeeks = s.due_weeks_before_trip ?? 12;
                return (
                  <Card key={s.setting_id} className={s.enabled ? '' : 'opacity-50'} data-testid={`reminder-${s.setting_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${s.enabled ? 'bg-primary/10' : 'bg-slate-100'}`}>
                            <Bell className={`w-4 h-4 ${s.enabled ? 'text-primary' : 'text-slate-400'}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-slate-800 text-sm">{taskLabel(s.reminder_type)}</p>
                              {!s.enabled && <Badge className="bg-slate-100 text-slate-500 border-0 text-xs">Paused</Badge>}
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">
                              Every <strong>{intervalWeeks} week{intervalWeeks !== 1 ? 's' : ''}</strong>
                              {' '}· Due <strong>{dueWeeks} weeks</strong> before trip
                            </p>
                            {s.message_template && (
                              <p className="text-xs text-slate-400 mt-1 italic truncate max-w-xs">"{s.message_template.slice(0, 80)}..."</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Switch checked={s.enabled !== false} onCheckedChange={() => handleToggle(s)} />
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500 hover:text-primary"
                            onClick={() => openEdit(s)}>Edit</Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-300 hover:text-red-500"
                            onClick={() => handleDelete(s.setting_id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Create / Edit Dialog */}
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingSetting ? 'Edit Reminder' : 'New Reminder'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Task */}
            <div>
              <Label className="mb-1.5 block">Task to remind about *</Label>
              <Select value={formData.reminder_type} onValueChange={v => set('reminder_type', v)}>
                <SelectTrigger data-testid="reminder-type-select"><SelectValue placeholder="Select a task..." /></SelectTrigger>
                <SelectContent>
                  {TASK_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      <div>
                        <p className="font-medium text-sm">{t.label}</p>
                        <p className="text-xs text-slate-400">{t.description}</p>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Two simple number fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block">Due date</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number" min="1" max="52"
                    value={formData.due_weeks_before_trip}
                    onChange={e => set('due_weeks_before_trip', parseInt(e.target.value) || 1)}
                    className="w-20 text-center"
                    data-testid="due-weeks-input"
                  />
                  <span className="text-sm text-slate-500 whitespace-nowrap">weeks before trip</span>
                </div>
              </div>
              <div>
                <Label className="mb-1.5 block">Remind every</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number" min="1" max="12"
                    value={formData.interval_weeks}
                    onChange={e => set('interval_weeks', parseInt(e.target.value) || 1)}
                    className="w-20 text-center"
                    data-testid="interval-weeks-input"
                  />
                  <span className="text-sm text-slate-500">weeks</span>
                </div>
              </div>
            </div>

            {/* Preview */}
            {previewText() && (
              <div className="bg-slate-50 rounded-xl p-3 text-xs text-slate-500">
                <span className="font-medium text-slate-700">Preview: </span>{previewText()}
              </div>
            )}

            {/* Custom message */}
            <div>
              <Label className="mb-1.5 block">
                Custom message <span className="text-slate-400 font-normal">(optional — leave blank for default)</span>
              </Label>
              <Textarea
                value={formData.message_template}
                onChange={e => set('message_template', e.target.value)}
                placeholder="Hi {name}, this is a reminder about your upcoming trip..."
                rows={4}
                className="resize-none text-sm"
              />
              <div className="bg-slate-50 rounded-lg px-3 py-2 mt-1.5 text-xs text-slate-500">
                <span className="font-medium text-slate-600">Available placeholders: </span>
                <code className="text-primary">{'{name}'}</code> participant name, 
                <code className="text-primary">{'{trip_name}'}</code> trip name, 
                <code className="text-primary">{'{trip_start}'}</code> trip start date, 
                <code className="text-primary">{'{days_until}'}</code> days until trip, 
                <code className="text-primary">{'{due_date}'}</code> task due date, 
                <code className="text-primary">{'{role}'}</code> work role, 
                <code className="text-primary">{'{pending_list}'}</code> bullet list of incomplete tasks
              </div>
            </div>

            {/* Enabled toggle */}
            <div className="flex items-center gap-3">
              <Switch checked={formData.enabled} onCheckedChange={v => set('enabled', v)} />
              <Label className="cursor-pointer">{formData.enabled ? 'Active — will send reminders' : 'Paused — no reminders sent'}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button className="bg-primary" onClick={handleSave} data-testid="save-reminder-btn">
              {editingSetting ? 'Save Changes' : 'Create Reminder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Reminder Job History</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto py-2">
            {jobHistory.length === 0 ? (
              <p className="text-center text-slate-400 py-6 text-sm">No runs yet. Click "Run Manually" to test.</p>
            ) : (
              jobHistory.map((job, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl text-sm">
                  <div>
                    <p className="font-medium text-slate-800">
                      {job.emails_sent} email{job.emails_sent !== 1 ? 's' : ''} sent
                    </p>
                    <p className="text-xs text-slate-400">
                      {job.settings_processed} settings · {job.trips_checked} trips checked
                    </p>
                  </div>
                  <p className="text-xs text-slate-400">{new Date(job.executed_at).toLocaleString()}</p>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
      </>
    </PageLayout>
  );
};

export default ReminderSettingsPage;
